/** Connector manifest setting blocks (settings / userSettings). */

export interface ConnectorSettingOption {
  value: string;
  name: string | { en: string; de: string };
}

/** Manifest `visibleWhen="<setting>=<value>"`, parsed by the connector. */
export interface SettingVisibility {
  setting: string;
  equals: string;
}

export interface ConnectorSetting {
  id: string;
  name: string | { en: string; de: string };
  description?: string | { en: string; de: string };
  type: 'string' | 'credential' | 'number' | 'boolean' | 'select';
  required?: boolean;
  default?: string | { en: string; de: string };
  adminOnly?: boolean;
  hidden?: boolean;
  options?: ConnectorSettingOption[] | null;
}

export interface ConnectorSettingGroup {
  id: string;
  name?: string | { en: string; de: string } | null;
  settings: ConnectorSetting[];
  hidden?: boolean;
  visibleWhen?: SettingVisibility | null;
}

export type ConnectorSettingBlock = 'settings' | 'userSettings';

/** Unconditional blocks stay visible; conditional ones need the controlling setting to match. */
export function settingVisibilityMet(
  visibleWhen: SettingVisibility | null | undefined,
  values: Record<string, string>,
): boolean {
  if (!visibleWhen) {
    return true;
  }
  return values[visibleWhen.setting] === visibleWhen.equals;
}

export function translateConnectorManifestValue(value: unknown, lang: string): string {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, string>;
    return obj[lang] ?? obj['en'] ?? '';
  }
  return String(value);
}

/** Manifest `moreInfo` holds the instructions-manual URL (http/https). */
export function connectorInstructionsManualUrl(
  manifest: Record<string, unknown> | undefined | null,
  lang: string,
): string | undefined {
  const url = translateConnectorManifestValue(manifest?.['moreInfo'], lang).trim();
  return url.startsWith('http') ? url : undefined;
}

export function connectorSettingGroups(
  manifest: Record<string, unknown> | undefined | null,
  block: ConnectorSettingBlock = 'settings',
): ConnectorSettingGroup[] {
  const groups = manifest?.[block];
  return Array.isArray(groups) ? (groups as ConnectorSettingGroup[]) : [];
}

export function connectorVisibleSettingGroups(
  manifest: Record<string, unknown> | undefined | null,
  block: ConnectorSettingBlock = 'settings',
  visibilityValues?: Record<string, string>,
): ConnectorSettingGroup[] {
  return connectorSettingGroups(manifest, block).filter((group) => {
    if (group.hidden) {
      return false;
    }
    if (!visibilityValues) {
      return true;
    }
    return settingVisibilityMet(group.visibleWhen, visibilityValues);
  });
}

/**
 * Setting values to evaluate `visibleWhen` against outside the settings form: the saved
 * config, falling back to manifest defaults so a not-yet-configured connector still
 * resolves its conditions (e.g. DocuWare defaults to cloud mode).
 */
export function connectorSettingValues(
  manifest: Record<string, unknown> | undefined | null,
  config: Record<string, unknown> | undefined | null,
  lang = 'en',
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const block of ['settings', 'userSettings'] as ConnectorSettingBlock[]) {
    for (const group of connectorSettingGroups(manifest, block)) {
      for (const setting of group.settings) {
        const saved = config?.[setting.id];
        values[setting.id] =
          typeof saved === 'string' && saved.length > 0
            ? saved
            : translateConnectorManifestValue(setting.default, lang);
      }
    }
  }
  return values;
}


export function connectorVisibleLocalComponents(
  manifest: Record<string, unknown> | undefined | null,
  config: Record<string, unknown> | undefined | null,
  lang = 'en',
): Record<string, unknown>[] {
  const setup = manifest?.['setup'] as Record<string, unknown> | undefined;
  const localComponents = setup?.['localComponents'];
  if (!Array.isArray(localComponents)) {
    return [];
  }
  const values = connectorSettingValues(manifest, config, lang);
  return localComponents.filter((component) =>
    settingVisibilityMet(
      (component as Record<string, unknown>)?.['visibleWhen'] as SettingVisibility | null,
      values,
    ),
  );
}

export function connectorHasUserSettings(
  manifest: Record<string, unknown> | undefined | null,
  adminConfig?: Record<string, unknown> | null,
  lang = 'en',
): boolean {
  const visibilityValues = connectorSettingValues(manifest, adminConfig, lang);
  return connectorVisibleSettingGroups(manifest, 'userSettings', visibilityValues).some(
    (group) => group.settings.length > 0,
  );
}

/** True when ``manifest.oauth.userOAuth`` is enabled (Kleos / Actaport / DocuWare cloud). */
export function connectorManifestSupportsUserOAuth(
  manifest: Record<string, unknown> | undefined | null,
): boolean {
  const oauth = manifest?.['oauth'];
  if (!oauth || typeof oauth !== 'object') {
    return false;
  }
  const flag = (oauth as Record<string, unknown>)['userOAuth'];
  if (typeof flag === 'string') {
    return ['true', '1', 'yes'].includes(flag.trim().toLowerCase());
  }
  return flag === true;
}

/**
 * User OAuth Connect is available when the manifest enables it and the firm is not
 * in on-prem mode (DocuWare on-prem uses username/password instead).
 */
export function connectorUserOAuthAvailable(
  manifest: Record<string, unknown> | undefined | null,
  adminConfig?: Record<string, unknown> | null,
  lang = 'en',
): boolean {
  if (!connectorManifestSupportsUserOAuth(manifest)) {
    return false;
  }
  const values = connectorSettingValues(manifest, adminConfig, lang);
  if (values['deployment_mode'] === 'on_premise') {
    return false;
  }
  return true;
}

/** Required userSettings ids from the currently visible user groups. */
export function connectorRequiredUserSettingIds(
  manifest: Record<string, unknown> | undefined | null,
  adminConfig?: Record<string, unknown> | null,
  lang = 'en',
): string[] {
  const visibilityValues = connectorSettingValues(manifest, adminConfig, lang);
  return connectorVisibleSettingGroups(manifest, 'userSettings', visibilityValues)
    .flatMap((group) => group.settings)
    .filter((setting) => setting.required)
    .map((setting) => setting.id);
}

/**
 * True when this user can use the connector for runtime ops (query/import).
 * Connectors without visible userSettings for the current mode are firm-wide /
 * OAuth-only → always linked once firm-active (OAuth checked separately in UI).
 */
export function connectorUserIsLinked(
  connector: {
    manifest_json?: Record<string, unknown> | null;
    instances?: {
      active?: boolean;
      config?: Record<string, unknown> | null;
      user_config?: Record<string, unknown> | null;
    }[] | null;
  },
): boolean {
  const instance =
    connector.instances?.find((inst) => inst?.active === true)
    ?? connector.instances?.[0]
    ?? null;
  const adminConfig = instance?.config ?? null;
  if (!connectorHasUserSettings(connector.manifest_json, adminConfig)) {
    return true;
  }
  const userConfig = instance?.user_config;
  if (!userConfig || typeof userConfig !== 'object') {
    return false;
  }
  const requiredIds = connectorRequiredUserSettingIds(connector.manifest_json, adminConfig);
  const ids =
    requiredIds.length > 0
      ? requiredIds
      : connectorVisibleSettingGroups(
          connector.manifest_json,
          'userSettings',
          connectorSettingValues(connector.manifest_json, adminConfig),
        ).flatMap((group) => group.settings.map((setting) => setting.id));
  if (ids.length === 0) {
    return false;
  }
  return ids.every((id) => {
    const value = userConfig[id];
    return typeof value === 'string' && value.trim().length > 0;
  });
}
